import type { Activity, MeetingView } from '@sorted/types'
import { CalendarPlus, Phone } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { ACTIVITY_ICON, Check, MapPin, Scale, Send } from '../components/icons.js'
import { Stars } from '../components/Stars.js'
import { CalendarDialog } from '../components/CalendarDialog.js'
import { googleCalendarUrl } from '../lib/calendar.js'
import { fireConfetti } from '../lib/confetti.js'
import { Screen } from '../components/Screen.js'
import { VenueMap } from '../components/VenueMap.js'
import { VenuePhoto } from '../components/VenuePhoto.js'
import {
  activityLabel,
  fairnessLabel,
  mapsUrl,
  meetingTime,
  ratingLabel,
  reviewsUrl,
  whatsappHref,
  whatsappText,
} from '../lib/format.js'
import { t } from '../lib/i18n.js'

/**
 * Confirmed - spec §9.
 *
 * "Tell them" is the primary CTA and the workhorse notification channel: it is
 * the one that always works, on every phone, with no push permission and no
 * account. Push and email are nudges layered on top, never load-bearing.
 */

/**
 * Activities where phoning ahead is a real step. A walk in the park has no
 * reservation, and a coffee rarely needs one.
 */
const BOOKABLE = new Set<Activity>(['dinner', 'drinks', 'brunch', 'beer_garden'])

interface Props {
  meeting: MeetingView
  token: string
}

export function Confirmed({ meeting, token }: Props) {
  const venue = meeting.venue
  const isCreator = meeting.role === 'creator'

  /**
   * Sorted. Fired once per mount, for whichever side is looking: the
   * recipient who just chose, and the creator whose 5s poll has just landed
   * on the answer she was waiting for. Both are the moment this product
   * exists to produce.
   *
   * Guarded because the creator's screen keeps polling underneath.
   */
  const [calendarOpen, setCalendarOpen] = useState(false)

  const celebrated = useRef(false)
  useEffect(() => {
    if (venue && !celebrated.current) {
      celebrated.current = true
      fireConfetti()
    }
  }, [venue])

  const activity = meeting.activity as Activity
  const Icon = ACTIVITY_ICON[activity]

  if (!venue) {
    return (
      <Screen centered>
        <h1>{t('done.title')}</h1>
        <p className="muted">{t('done.pending')}</p>
      </Screen>
    )
  }

  const shareUrl = `${window.location.origin}/${token}`
  const message = whatsappText(venue, activity, meeting.meetAt, shareUrl)
  const rating = ratingLabel(venue)

  return (
    <Screen>
      <div className="center done-mark">
        <span className="tick" aria-hidden>
          <Check size={26} strokeWidth={2.5} />
        </span>
        <h1>{t('done.title')}</h1>
      </div>

      <div className="card enter">
        <VenuePhoto
          token={token}
          placeId={venue.placeId}
          hasPhoto={venue.hasPhoto}
          name={venue.name}
        />

        <h2 className="venue-name">{venue.name}</h2>

        <div className="venue-meta">
          <span className="item">
            <Icon size={15} aria-hidden />
            {meetingTime(meeting.meetAt)}
          </span>
          {rating && venue.rating !== null && (
            <a
              className="item rating-link"
              href={reviewsUrl(venue.placeId)}
              target="_blank"
              rel="noreferrer"
            >
              <Stars rating={venue.rating} size={13} />
              {rating}
            </a>
          )}
        </div>

        {/* Only when there is fairness to state. A pinned-area meeting routed
            nobody, so there is no comparison and no row — see VenueCard. */}
        {venue.etaCreatorS !== null && venue.etaRecipientS !== null && (
          <div className="fairness">
            <Scale size={16} aria-hidden />
            <div>
              <div className="fairness-line">
                {fairnessLabel(venue.etaCreatorS, venue.etaRecipientS, isCreator)}
              </div>
              <div className="faint">{t('fair.detail')}</div>
            </div>
          </div>
        )}

        <VenueMap lat={venue.location.lat} lng={venue.location.lng} name={venue.name} />
      </div>

      <div className="stack mt">
        {/* The workhorse. Primary for Bob; Alice already knows. */}
        {!isCreator && (
          <a
            className="btn btn-primary"
            href={whatsappHref(message)}
            target="_blank"
            rel="noreferrer"
          >
            <Send size={18} aria-hidden />
            {t('done.tell')}
          </a>
        )}

        {/* Reservation call. Only for the activities where booking is a real
            step - nobody phones ahead for a walk in the park - and only once
            confirmed, since calling about a place still being chosen makes no
            sense. `tel:` opens the dialler with the number filled in. */}
        {venue.phone && BOOKABLE.has(activity) && (
          <a className="btn" href={`tel:${venue.phone.replace(/\s/g, '')}`}>
            <Phone size={17} aria-hidden />
            {t('done.call')}
          </a>
        )}

        <div className="btn-row">
          <a
            className="btn"
            href={mapsUrl(venue.placeId, venue.name)}
            target="_blank"
            rel="noreferrer"
          >
            <MapPin size={17} aria-hidden />
            {t('done.directions')}
          </a>
          {/* Asks WHERE rather than downloading a file: on Android a bare
              .ics lands in the notification shade and reads as nothing
              having happened. */}
          <button className="btn" onClick={() => setCalendarOpen(true)}>
            <CalendarPlus size={17} aria-hidden />
            {t('done.calendar')}
          </button>
        </div>

        {/* The growth loop - §16 calls it the only growth vector, and the
            recipient is the best candidate for it: they have just seen the
            whole thing work, from the side that needed no account.
            Deliberately AFTER the decision and deliberately quiet: a pitch
            before the confirm would compete with the one thing this screen
            exists to do. */}
        {!isCreator && (
          <p className="pitch faint">
            {t('done.pitch')}{' '}
            <a href="/">{t('done.pitchCta')}</a>
          </p>
        )}
      </div>

      <CalendarDialog
        open={calendarOpen}
        onOpenChange={setCalendarOpen}
        googleUrl={googleCalendarUrl({
          title: `${activityLabel(activity)}: ${venue.name}`,
          meetAt: meeting.meetAt,
          location: venue.name,
          url: shareUrl,
        })}
        icsUrl={`/api/meetings/${token}/calendar.ics`}
      />
    </Screen>
  )
}
